import React, { useEffect, useRef, useState } from 'react';
import { Input, InputGroup, InputGroupAddon } from 'reactstrap';
import { FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';

function MultiSelectDropdown({
    options,
    selectedValues,
    onToggleOption,
    onClear,
    placeholder,
    disabled = false,
    allLabel,
    onToggleAll,
}) {
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!open) setSearchText('');
    }, [open]);

    const normalizedSearch = searchText.trim().toLowerCase();
    const filteredOptions = normalizedSearch
        ? options.filter((option) =>
              option.label
                  .toLowerCase()
                  .split(/[^a-z0-9]+/i)
                  .some((word) => word.startsWith(normalizedSearch))
          )
        : options;

    const allSelected = Boolean(allLabel) && options.length > 0 && selectedValues.length === options.length;

    const displayLabel = () => {
        if (selectedValues.length === 0) return '';
        if (allLabel && allSelected) return allLabel;
        if (selectedValues.length === 1) {
            return options.find((option) => option.value === selectedValues[0])?.label || '';
        }
        return `${selectedValues.length} Selected`;
    };

    const label = displayLabel();
    const toggleOpen = () => {
        if (!disabled) setOpen((prev) => !prev);
    };

    return (
        <div className="position-relative" ref={containerRef}>
            <InputGroup>
                <Input
                    type="text"
                    readOnly
                    disabled={disabled}
                    value={label}
                    placeholder={placeholder}
                    onClick={toggleOpen}
                    style={{ cursor: disabled ? 'default' : 'pointer', backgroundColor: disabled ? '#e9ecef' : 'transparent' }}
                />
                {selectedValues.length > 0 && (
                    <InputGroupAddon addonType="append">
                        <span
                            className="input-group-text"
                            style={{
                                cursor: 'pointer',
                                borderLeft: 'none',
                                borderRight: 'none',
                                backgroundColor: '#fff',
                                color: '#adb5bd',
                            }}
                            title="Clear selection"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClear();
                                setOpen(false);
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#495057';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#adb5bd';
                            }}>
                            <FaTimes size={12} />
                        </span>
                    </InputGroupAddon>
                )}
                <InputGroupAddon addonType="append">
                    <span
                        className="input-group-text bg-light"
                        style={{ cursor: disabled ? 'default' : 'pointer', borderLeft: 'none', opacity: disabled ? 0.65 : 1 }}
                        onClick={toggleOpen}>
                        {open ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                </InputGroupAddon>
            </InputGroup>

            {open && !disabled && (
                <div className="pharmacy-reports-multiselect-panel">
                    <div className="p-2" style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                        <Input
                            type="text"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            autoFocus
                            autoComplete="off"
                        />
                    </div>
                    {allLabel && (
                        <label className="pharmacy-reports-multiselect-option pharmacy-reports-multiselect-all">
                            <input type="checkbox" checked={allSelected} onChange={onToggleAll} />
                            {allLabel}
                        </label>
                    )}
                    <div className="pharmacy-reports-multiselect-list">
                        {filteredOptions.map((option) => (
                            <label key={option.value} className="pharmacy-reports-multiselect-option">
                                <input
                                    type="checkbox"
                                    checked={selectedValues.includes(option.value)}
                                    onChange={() => onToggleOption(option.value)}
                                />
                                {option.label}
                            </label>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className="px-2 py-1 text-muted">No matches</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default MultiSelectDropdown;
